/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components

import Card from "@mui/material/Card";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import breakpoints from "assets/theme/base/breakpoints";

// Data
// import authorsTableData from "layouts/tables/data/authorsTableData";
//import ParentsTableData from "Views/people/parents/data/parentsTableData";
import projectsTableData from "layouts/tables/data/projectsTableData";
// import ClassMgtTableData from "Views/Activities/ClassMgt/data/classMgtTableData";

// import tutorsParollTableData from "Views/Activities/TutorsPayroll/data/tutorsPayrollTableData"

// import DraftTutorePayrollTableData from "Views/FinanceManagment/DraftTutorSalary/data/draftTutorSalaryTableData";

// import supportTicketTableData from "Views/Activities/SupportTicket/data/supportTicketTableData";

import { useState, useEffect } from "react";
import ApnaTabs from "components/ApnaTabs/ApnaTabs";
import tabs from "assets/theme/components/tabs";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import Dropdown from "components/CustomDropdown/Dropdown";
import Invoices from "layouts/billing/components/Invoices";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import dayjs from "dayjs";
import CustomDatePicker from "components/CustomDatePicker/CustomDatePicker";
import FileUploader from "components/FileUploader/FileUploader";
import api from "utils/api";
import ArgonBadge from "components/ArgonBadge";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import PaginationBar from "components/PaginationBar/PaginationBar";
import { formatCycleLabel } from "utils/formatCycleLabel";

function Tables() {
  const [aadhaarFiles, setAadhaarFiles] = useState([]);

  const [open, setOpen] = useState(false);
  const handleAddClick = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const [dob, setDob] = useState(dayjs());

  // Tab change handler
  const handleTabChange = (status) => {
    setActiveTab(status);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    // Refetch data immediately with new status
    fetchInvoices(1, pagination.limit, search, status);
  };

  // Calculate dynamic tab data
  const getTabData = () => {
    return [
      { label: "All", count: statusCounts.ALL, color: "#2027e1ff", content: "", status: "All" },
      {
        label: "Pending",
        count: statusCounts.PENDING,
        color: "#ec10c7ff",
        content: "",
        status: "PENDING",
      },
      { label: "Paid", count: statusCounts.PAID, color: "#10a6ecff", content: "", status: "PAID" },
      {
        label: "Failed",
        count: statusCounts.FAILED,
        color: "#ec3c10ff",
        content: "",
        status: "FAILED",
      },
      { label: "Void", count: statusCounts.VOID, color: "#1a6808b7", content: "", status: "VOID" },
    ];
  };

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [search, setSearch] = useState("");

  // New state for API data
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
    limit: 10,
  });
  const [statusCounts, setStatusCounts] = useState({
    ALL: 0,
    PENDING: 0,
    PAID: 0,
    FAILED: 0,
    VOID: 0,
  });
  const [activeTab, setActiveTab] = useState("All");

  // Fetch invoices from API
  const fetchInvoices = async (
    page = 1,
    limit = pagination.limit,
    searchQuery = search,
    tabStatus = activeTab
  ) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
      };

      if (fromDate) params.from = fromDate.format("YYYY-MM-DD");
      if (toDate) params.to = toDate.format("YYYY-MM-DD");
      if (searchQuery) params.search = searchQuery;
      if (tabStatus !== "All") params.status = tabStatus;

      const response = await api.get("/finance/get-all-raise-invoice", { params });

      if (response.data.success) {
        // Calculate gross margin for each invoice
        const invoicesWithGrossMargin = response.data.data.map((invoice) => {
          const tutorSalary = invoice.classSessionId?.[0]?.tutorSalary || 0;
          const totalClasses = invoice.totalClasses || 0;
          const totalAmount = invoice.totalAmount || 0;
          const calculatedGrossMargin = totalAmount - tutorSalary * totalClasses;
          const grossMarginPercentage =
            totalAmount > 0 ? ((calculatedGrossMargin / totalAmount) * 100).toFixed(2) : 0;

          return {
            ...invoice,
            grossMargin: calculatedGrossMargin,
            grossMarginPercentage: grossMarginPercentage,
          };
        });

        setInvoices(invoicesWithGrossMargin);
        setPagination(response.data.pagination);
        setStatusCounts(response.data.statusCounts);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setLoading(false);
    }
  };

  // Effect to fetch data on mount and filter changes
  useEffect(() => {
    fetchInvoices();
  }, [fromDate, toDate]);

  // Pagination handlers
  const handlePageChange = (event, page) => {
    fetchInvoices(page);
  };

  const handleRowsPerPageChange = (event) => {
    const newLimit = event.target.value;
    setPagination((prev) => ({ ...prev, limit: newLimit, currentPage: 1 }));
    fetchInvoices(1, newLimit);
  };

  // Search handler
  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    fetchInvoices(1);
  };

  // Search on enter key
  const handleSearchKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  // Reset date filters
  const handleResetDates = () => {
    setFromDate(null);
    setToDate(null);
  };

  // View/Download invoice handler - Opens PDF in new tab for preview with download option
  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const response = await api.get(`/finance/invoices/${invoiceId}/download`, {
        responseType: "blob",
      });

      // Create a blob URL and open in new tab for preview
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      // Open PDF in new tab - browser will show preview with download option
      const newWindow = window.open(url, "_blank");

      // Clean up the URL after window is loaded (give more time)
      if (newWindow) {
        newWindow.onload = () => {
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
          }, 5000);
        };
      } else {
        // If popup blocked, cleanup after longer delay
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 10000);
      }
    } catch (error) {
      console.error("Error opening invoice:", error);
      alert("Failed to open invoice. Please try again.");
    }
  };

  // Build table rows from API data
  const buildTableRows = () => {
    return invoices.map((invoice, index) => ({
      sno: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {(pagination.currentPage - 1) * pagination.limit + index + 1}
        </ArgonTypography>
      ),
      "client name": (
        <ArgonBox display="flex" alignItems="center" px={0.5} py={0.1}>
          <ArgonBox display="flex" flexDirection="column">
            <ArgonTypography variant="button" fontWeight="medium">
              {invoice.parentId?.fullName || "N/A"}
            </ArgonTypography>
            <ArgonTypography variant="caption" color="secondary">
              {invoice.parentId?.email || ""}
            </ArgonTypography>
          </ArgonBox>
        </ArgonBox>
      ),
      cycle: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {formatCycleLabel(invoice.totalCycles || invoice.classSessionId?.[0]?.noOfMonths)}
        </ArgonTypography>
      ),
      // Amount: (
      //   <ArgonTypography variant="caption" color="text" fontWeight="medium">
      //     ₹{invoice.totalTutorPay || 0}
      //   </ArgonTypography>
      // ),
      "date & time sent": (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {dayjs(invoice.createdAt).format("DD MMM YYYY, hh:mm A")}
        </ArgonTypography>
      ),
      "Total Amount": (
        <ArgonTypography variant="caption" color="success" fontWeight="medium">
          ₹{invoice.totalAmount || 0}
        </ArgonTypography>
      ),
      "Gross Margin": (
        <ArgonTypography variant="caption" fontWeight="medium">
          <span style={{ color: "#17c1e8" }}>₹{invoice.grossMargin || 0}</span>{" "}
          <span style={{ color: "#67748E" }}>({invoice.grossMarginPercentage || 0}%)</span>
        </ArgonTypography>
      ),
      class: (
        <ArgonTypography variant="caption" color="text" fontWeight="medium">
          {invoice.students?.[0]?.class?.name || "N/A"}
        </ArgonTypography>
      ),
      status: (
        <ArgonBadge
          variant="gradient"
          badgeContent={invoice.status}
          color={
            invoice.status === "PAID"
              ? "success"
              : invoice.status === "PENDING"
              ? "warning"
              : "error"
          }
          size="xs"
          container
        />
      ),
      action: (
        <ArgonBox display="flex" alignItems="center" gap={1}>
          <ArgonTypography
            component="a"
            href="#"
            variant="body1"
            color="primary"
            fontWeight="medium"
            onClick={(e) => {
              e.preventDefault();
              handleDownloadInvoice(invoice._id);
            }}
            sx={{ cursor: "pointer" }}
          >
            <DownloadIcon />
          </ArgonTypography>
          <ArgonTypography component="a" href="#" variant="body1" color="info" fontWeight="medium">
            <VisibilityIcon />
          </ArgonTypography>
          <ArgonTypography component="a" href="#" variant="body1" color="error" fontWeight="medium">
            <DeleteIcon />
          </ArgonTypography>
        </ArgonBox>
      ),
    }));
  };

  const tableData = {
    columns: [
      { name: "sno", align: "center" },
      { name: "client name", align: "left" },
      { name: "cycle", align: "left" },
      // { name: "Amount", align: "left" },
      { name: "date & time sent", align: "left" },
      { name: "Total Amount", align: "left" },
      { name: "Gross Margin", align: "left" },
      { name: "class", align: "center" },
      { name: "status", align: "center" },
      { name: "action", align: "center" },
    ],
    rows: buildTableRows(),
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonBox display="flex" justifyContent="end" alignItems="center" mb={2}></ArgonBox>
              <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                Raise invoice
              </ArgonTypography>

              <ArgonBox sx={{ width: "fit-content" }}>
                <ApnaTabs tabsData={getTabData()} onTabChange={handleTabChange} />
              </ArgonBox>

              <ArgonBox p={2}>
                <ArgonBox
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  gap={2} // equal horizontal + vertical gap
                >
                  <ArgonBox display="flex" alignItems="center" gap={2}>
                    {/* Label */}
                    <ArgonTypography variant="subtitle2" fontWeight="bold" color="text">
                      Filters
                    </ArgonTypography>

                    <CustomDatePicker
                      label="From"
                      value={fromDate}
                      onChange={(newValue) => setFromDate(newValue)}
                      width={160}
                      maxDate={dayjs()} // future date disable
                    />
                    <CustomDatePicker
                      label="To"
                      value={toDate}
                      onChange={(newValue) => setToDate(newValue)}
                      width={160}
                      maxDate={dayjs()} // future date disable
                      minDate={fromDate}
                    />

                    {/* Reset Button */}
                    <ArgonButton
                      variant="contained"
                      size="small"
                      color="primary"
                      onClick={handleResetDates}
                      disabled={!fromDate && !toDate}
                    >
                      Reset
                    </ArgonButton>
                  </ArgonBox>

                  <ArgonBox display="flex" alignItems="center" gap={2}>
                    {/* Search Field */}
                    <ArgonBox sx={{ minWidth: "250px" }}>
                      <ArgonInput
                        fullWidth
                        size="small"
                        placeholder="Search by Name, Email, Cycle"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onKeyPress={handleSearchKeyPress}
                      />
                    </ArgonBox>

                    {/* Search Button */}
                    <ArgonButton
                      variant="contained"
                      size="small"
                      color="primary"
                      sx={{ minWidth: "100px" }}
                      onClick={handleSearch}
                    >
                      Search
                    </ArgonButton>

                    {/* Add Button
                    <ArgonButton
                      variant="contained"
                      size="small"
                      color="primary"
                      sx={{ minWidth: "100px" }}
                      onClick={handleAddClick}
                    >
                      Add
                    </ArgonButton> */}
                  </ArgonBox>
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>
            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    Fetching invoices...
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Please wait while we load the latest data.
                  </ArgonTypography>
                </ArgonBox>
              ) : invoices.length > 0 ? (
                <Table columns={tableData.columns} rows={tableData.rows} />
              ) : (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    {activeTab === "All"
                      ? "No invoices found"
                      : `No ${activeTab.toLowerCase()} invoices found`}
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    {activeTab === "All"
                      ? "Try adjusting your filters or check back later."
                      : `No invoices with ${activeTab.toLowerCase()} status found.`}
                  </ArgonTypography>
                </ArgonBox>
              )}
            </ArgonBox>

            {/* Pagination Controls */}
            {invoices.length > 0 && (
              <PaginationBar
                currentPage={pagination.currentPage}
                rowsPerPage={pagination.limit}
                totalItems={pagination.totalRecords}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            )}
          </Card>

          {/* Modal */}
          <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
              style: {
                borderRadius: 20,
                padding: "20px",
                background: "#f9fafc", // light background for better contrast
              },
            }}
          >
            {/* Header */}
            <DialogTitle sx={{ pb: 1 }}>
              <ArgonTypography variant="h4" fontWeight="bold" color="info">
                🧾 Raise Invoice
              </ArgonTypography>
              <ArgonTypography variant="body2" color="text" mt={0.5}>
                Fill in the details below to generate a new invoice
              </ArgonTypography>
            </DialogTitle>

            {/* Body */}
            <DialogContent dividers>
              <form>
                <Grid container spacing={3}>
                  {/* Invoice ID */}
                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="subtitle2" color="text" fontWeight="medium" mb={1}>
                      Invoice ID
                    </ArgonTypography>
                    <ArgonInput fullWidth placeholder="Enter Invoice ID" name="invoice_number" />
                  </Grid>

                  {/* Client Dropdown */}
                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="subtitle2" color="text" fontWeight="medium" mb={1}>
                      Client
                    </ArgonTypography>
                    <Dropdown options={["Client 1", "Client 2"]} width="100%" />
                  </Grid>

                  {/* Class Dropdown */}
                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="subtitle2" color="text" fontWeight="medium" mb={1}>
                      Class
                    </ArgonTypography>
                    <Dropdown options={["Class 1", "Class 2"]} width="100%" />
                  </Grid>

                  {/* Cycle */}
                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="subtitle2" color="text" fontWeight="medium" mb={1}>
                      Cycle
                    </ArgonTypography>
                    <ArgonInput fullWidth placeholder="Enter Cycle" />
                  </Grid>

                  {/* Total Amount */}
                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="subtitle2" color="text" fontWeight="medium" mb={1}>
                      Total Amount
                    </ArgonTypography>
                    <ArgonInput fullWidth placeholder="Enter Amount" name="total_amount" />
                  </Grid>

                  {/* Due Amount */}
                  <Grid item xs={12} md={4}>
                    <ArgonTypography variant="subtitle2" color="text" fontWeight="medium" mb={1}>
                      Due Amount
                    </ArgonTypography>
                    <ArgonInput
                      fullWidth
                      placeholder="Enter Amount"
                      name="due_amount"
                      defaultValue="0"
                    />
                  </Grid>

                  {/* Invoice Amount */}
                  <Grid item xs={12} md={6}>
                    <ArgonTypography variant="subtitle2" color="text" fontWeight="medium" mb={1}>
                      Invoice Amount
                    </ArgonTypography>
                    <ArgonInput fullWidth placeholder="Enter Amount" name="invoice_amount" />
                  </Grid>

                  {/* Upload Invoice */}
                  <Grid item xs={12} md={6}>
                    <FileUploader
                      label="Upload Invoice"
                      files={aadhaarFiles}
                      setFiles={setAadhaarFiles}
                      accept="image/*,application/pdf"
                      multiple={false}
                      Width="100%"
                      Height={120}
                    />
                  </Grid>

                  {/* Notes */}
                  <Grid item xs={12}>
                    <ArgonTypography variant="subtitle2" color="text" fontWeight="medium" mb={1}>
                      Note
                    </ArgonTypography>
                    <ArgonInput multiline rows={3} placeholder="Enter Note" fullWidth />
                  </Grid>
                </Grid>

                {/* Footer Buttons */}
                <ArgonBox display="flex" justifyContent="flex-end" gap={2} mt={4}>
                  <ArgonButton
                    variant="outlined"
                    color="secondary"
                    onClick={handleClose}
                    sx={{ px: 4, py: 1.2, borderRadius: "10px" }}
                  >
                    Cancel
                  </ArgonButton>

                  <ArgonButton
                    variant="gradient"
                    color="info"
                    type="submit"
                    sx={{ px: 4, py: 1.2, borderRadius: "10px", fontWeight: "bold" }}
                  >
                    Save Invoice
                  </ArgonButton>
                </ArgonBox>
              </form>
            </DialogContent>
          </Dialog>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
